<template>
  <div class="qaqc-test-plot-wrapper">
    <div class="qaqc-test-chart-name-top">
      <h6>{{ title }}</h6>
      <div class="qaqc-test-header-actions">
        <button
          type="button"
          class="qaqc-chart-type-btn"
          :class="{ 'is-active': chartType === 'bar' }"
          aria-label="Bar chart"
          @click="setChartType('bar')"
        >
          <v-icon size="16" icon="mdi-chart-bar" />
        </button>
        <button
          type="button"
          class="qaqc-chart-type-btn"
          :class="{ 'is-active': chartType === 'donut' }"
          aria-label="Donut chart"
          @click="setChartType('donut')"
        >
          <v-icon size="16" icon="mdi-chart-donut" />
        </button>
        <div
          class="csv-btn csv-btn--plot"
          role="button"
          aria-label="Download plot as PNG"
          @click="downloadChartPng"
        >
          <img
            class="pointer csv-btn-plot-icon"
            :src="plotImageDownload"
            alt="download plot as image"
          />
        </div>
      </div>
    </div>
    <div class="qaqc-test-plots-content">
      <div ref="chartContainer" class="qaqc-test-main-chart qaqc-status-bar-chart"></div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import { mapGetters } from "vuex";
import { applyPlotlyGridLineStroke, mergePlotlyTheme, plotlyThemeColors } from "@/utils/chartTheme";
import { downloadPlotlyHostAsPng } from "./plotlyOverviewImageDownload.js";

const STATUS_BAR_COLORS = {
  Passed: { light: "#079455", dark: "#6ce9a6" },
  "Needs Review": { light: "#98A2B3", dark: "#98A2B3" },
  Failed: { light: "#d92d20", dark: "#fda29b" },
};
const DEFAULT_BAR_COLOR = { light: "#2298D5", dark: "#2298D5" };

export default {
  name: "QCStatusOverviewChart",
  data() {
    return {
      plotImageDownload: require("@/assets/Svg/camIcon.svg"),
      chartType: "bar",
    };
  },
  props: {
    title: {
      type: String,
      default: "QC Status Overview",
    },
    data: {
      type: Object,
      default: () => ({
        categories: [],
        values: [],
      }),
    },
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
  },
  watch: {
    "$store.state.darkMode.isDarkMode"() {
      this.$nextTick(() => this.renderChart());
    },
    data: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.renderChart();
      this.bindChartResize();
    });
  },
  beforeUnmount() {
    this.unbindChartResize();
    if (this.$refs.chartContainer) {
      try {
        Plotly.purge(this.$refs.chartContainer);
      } catch (_) {}
    }
  },
  methods: {
    bindChartResize() {
      this.unbindChartResize();
      const el = this.$refs.chartContainer;
      if (!el) return;
      let ticking = false;
      this._onChartResize = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.resizeChart();
        });
      };
      window.addEventListener("resize", this._onChartResize);
      const host = el.parentElement || el;
      if (typeof ResizeObserver !== "undefined") {
        this._chartResizeObserver = new ResizeObserver(this._onChartResize);
        this._chartResizeObserver.observe(host);
      }
    },
    unbindChartResize() {
      if (this._onChartResize) {
        window.removeEventListener("resize", this._onChartResize);
        this._onChartResize = null;
      }
      if (this._chartResizeObserver) {
        this._chartResizeObserver.disconnect();
        this._chartResizeObserver = null;
      }
    },
    resizeChart() {
      const el = this.$refs.chartContainer;
      if (!el) return;
      try {
        Plotly.Plots.resize(el);
      } catch (_) {}
    },
    setChartType(type) {
      if (this.chartType === type) return;
      this.chartType = type;
      this.$nextTick(() => this.renderChart());
    },
    async downloadChartPng() {
      const categories = this.data.categories || [];
      const values = this.data.values || [];
      if (categories.length === 0 || values.length === 0) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "No plot to export.",
          color: "#374151",
          timeout: 4000,
        });
        return;
      }
      const el = this.$refs.chartContainer;
      const theme = plotlyThemeColors(this.isDarkMode);
      const exportBg = this.isDarkMode ? theme.sceneBg : theme.plotBg;
      const slug = String(this.title || "qc_status_overview")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const ok = await downloadPlotlyHostAsPng(el, `qaqc_${slug}_plot`, {
        paperBgcolor: exportBg,
        plotBgcolor: exportBg,
        title: this.title,
        isDarkMode: this.isDarkMode,
      });
      if (!ok) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "Plot image export failed.",
          color: "#B71C1C",
          timeout: 5000,
        });
      }
    },
    renderChart() {
      if (!this.$refs.chartContainer) return;
      const categories = this.data.categories || [];
      const values = this.data.values || [];
      if (categories.length === 0 || values.length === 0) return;
      const theme = plotlyThemeColors(this.isDarkMode);
      const lineColor = this.isDarkMode ? "#6b7280" : "#9ca3af";
      const tickColor = theme?.title || (this.isDarkMode ? "#e5e7eb" : "#344054");
      const barColors = categories.map((c) => {
        const entry = STATUS_BAR_COLORS[c] || DEFAULT_BAR_COLOR;
        return this.isDarkMode ? entry.dark : entry.light;
      });
      const traces =
        this.chartType === "donut"
          ? [
              {
                labels: categories,
                values,
                type: "pie",
                hole: 0.62,
                marker: { colors: barColors },
                textinfo: "none",
                hoverinfo: "label+value+percent",
                sort: false,
              },
            ]
          : [
              {
                x: categories,
                y: values,
                type: "bar",
                marker: { color: barColors },
              },
            ];
      const layout = mergePlotlyTheme(
        this.chartType === "donut"
          ? {
              autosize: true,
              margin: { t: 8, r: 16, b: 8, l: 16 },
              showlegend: true,
              legend: {
                orientation: "v",
                x: 1,
                xanchor: "left",
                y: 0.5,
                font: { size: 12 },
              },
            }
          : {
              autosize: true,
              margin: { t: 12, r: 8, b: 52, l: 40 },
              showlegend: false,
              xaxis: {
                title: "",
                tickfont: { size: 13, color: tickColor, family: "Inter, PT Sans, sans-serif" },
                tickangle: -32,
                automargin: true,
                showgrid: false,
                showline: true,
                linecolor: lineColor,
                linewidth: 1,
                zeroline: false,
              },
              yaxis: {
                title: "",
                tickfont: { size: 11 },
                rangemode: "tozero",
                showgrid: true,
                gridwidth: 1,
                griddash: "dot",
                zeroline: true,
                zerolinecolor: lineColor,
                showline: true,
                linecolor: lineColor,
                linewidth: 1,
              },
              barmode: "group",
              bargap: 0.25,
              bargroupgap: 0.05,
            },
        this.isDarkMode
      );
      const config = { responsive: true, displayModeBar: false };
      const el = this.$refs.chartContainer;
      Plotly.react(el, traces, layout, config).then(() => {
        applyPlotlyGridLineStroke(el, this.isDarkMode);
        this.resizeChart();
      });
    },
  },
};
</script>
